import { useEffect, useState } from "react";
import {
    LocalChangeCommitterState,
    LocalChangeCommitterStateProps,
} from "./LocalChangeCommitter.types";
import { useSelector, useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { createTaskListItemStatusAsync, EnhancedAxiosError, updateTaskListItemStatusAsync, deleteTaskListItemStatusAsync } from "src/api";
import { taskListItemStatusCreated, removeFromQualityHubOfflineQueue, taskListItemStatusDeleted, taskListItemStatusUpdated } from "src/redux";

async function sendQualityHubChange(
    qualityHubChanges: Spintr.AppState["qualityHub"]["offlineQueue"],
    dispatch: Dispatch,
): Promise<void> {
    if (!qualityHubChanges || qualityHubChanges.operations.length === 0) {
        return;
    }

    const change = qualityHubChanges.operations[0];
    try
    {
        switch (change.type) {
        case "CREATE_STATUS": {
            const createdStatus = await createTaskListItemStatusAsync(change.data.model);
            dispatch(taskListItemStatusCreated(createdStatus, change.data.listId));
            break;
        }
        case "UPDATE_STATUS": {
            const updatedStatus = await updateTaskListItemStatusAsync(
                change.data.status.id,
                change.data.model
            );
            dispatch(taskListItemStatusUpdated(updatedStatus, change.data.listId));
            break;
        }
        case "DELETE_STATUS": {
            await deleteTaskListItemStatusAsync(
                change.data.status.id,
            );
            dispatch(taskListItemStatusDeleted(change.data.status.id, change.data.listId));
            break;
        }}
    } catch (err) {
        const error = err as EnhancedAxiosError;
        if (error.isAxiosError && error.isOffline) {
            return;
        }
    }

    dispatch(removeFromQualityHubOfflineQueue(change.id));
}

/**
 * A component that keeps track of the online/offline status of the user
 * and commits local changes to the server when the user is online.
 *
 * @returns {null} Returns null as it does not render any UI
 */
function LocalChangeCommitter(): null {
    const [state, setState] = useState<LocalChangeCommitterState>({
        isOffline: false,
    });
    const { isOffline } = state;

    const dispatch = useDispatch();

    const {
        qualityHubChanges,
    } = useSelector<Spintr.AppState, LocalChangeCommitterStateProps>(
        (state) => ({
            qualityHubChanges: state.qualityHub.offlineQueue,
        }),
    );

    useEffect(function _LocalChangeCommitterUpdate() {
        if (isOffline) {
            return;
        }

        if (qualityHubChanges.operations.length > 0) {
            sendQualityHubChange(qualityHubChanges, dispatch);
        }
    
        // Add an else if in case you want to handle more stuff in here,
        // the effect should run again after the change is sent and the item
        // is removed from the queue.

    }, [dispatch, isOffline, qualityHubChanges]);

    useEffect(function _LocalChangeCommitterMount() {
        const interval = function _LocalChangeCommitterInterval() {
            setState((prevState) => ({
                ...prevState,
                isOffline: !navigator.onLine,
            }));
        };

        const intervalId = setInterval(interval, 1000 * 60);

        return function _LocalChangeCommitterUnmount() {
            clearInterval(intervalId);
        };
    }, []);

    return null;
}

export default LocalChangeCommitter;

